import { Component, Input, computed, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';

interface CultureOption {
  code: string;
  label: string;
}

const SUPPORTED_CULTURES = ['fa-IR', 'en-US'] as const;

@Component({
  standalone: true,
  selector: 'ain-culture-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CultureSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="code"
      [placeholder]="t(placeholderKey)"
      [showClear]="showClear"
      [filter]="false"
      [disabled]="disabled"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`.selector { width: 100%; }`]
})
export class CultureSelectorComponent implements ControlValueAccessor {
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() placeholderKey = 'catalog.selectors.culture.placeholder';
  @Input() showClear = false;
  @Input() appendTo: 'body' | 'self' = 'body';

  readonly options = computed<CultureOption[]>(() => {
    // Depend on loaded resources so labels refresh after localization loads/switches.
    this.localization.resources();
    return SUPPORTED_CULTURES.map(code => ({
      code,
      label: this.localization.translate(`catalog.selectors.culture.${code}`)
    }));
  });
  readonly value = signal<string | null>(null);
  disabled = false;

  writeValue(value: string | null): void {
    this.value.set(value?.trim() || null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
